<div class="site-analytics-summary-layout-container tw-relative tw-overflow-y-auto tw-h-full">
    <div class="site-analytics-summary-layout-content">
        <mon-page-layout
            [header]="config.pageHeaderKey | transloco"
            [subheader]="config.pageSubheaderKey | transloco"
        >
            <div class="site-analytics-summary__controls">
                <div class="site-analytics-summary__control">
                    <label for="site-analytics-summary-date-range">
                        {{ config.dateRangeLabelKey | transloco }}
                    </label>
                    <mon-site-analytics-date-range-picker
                        inputId="site-analytics-summary-date-range"
                        [ngModel]="primaryDateRange"
                        (ngModelChange)="onPrimaryDateRangeChange($event)"
                        data-test="site-analytics-summary-date-range"
                    />
                </div>
            </div>

            @if (loading) {
                <div class="site-analytics-summary__loading" data-test="site-analytics-summary-loading">
                    <p-progress-spinner ariaLabel="loading" />
                </div>
            } @else {
                @if (config.mode === 'visitors') {
                    <div class="site-analytics-summary__visitors-grid" data-test="site-analytics-summary-visitors-grid">
                        @for (card of visitorsCards; track card.id) {
                            <p-card styleClass="tw-h-full">
                                <ng-template #title>
                                    <div class="tw-text-lg tw-font-medium">{{ card.titleKey | transloco }}</div>
                                </ng-template>

                                @if (card.hasData) {
                                    <div class="site-analytics-summary__chart-container"
                                         [ngClass]="{
                                             'bubble-chart-container': card.chartType === 'bubble',
                                             'doughnut-chart-container': card.chartType === 'doughnut',
                                         }"
                                    >
                                        <p-chart
                                            [type]="card.chartType"
                                            [data]="card.chartData"
                                            [options]="card.chartOptions"
                                            [width]="card.chartType === 'doughnut' ? '200px' : '100%'"
                                            [height]="card.chartType === 'doughnut' ? undefined : '100%'"
                                        />
                                    </div>
                                } @else {
                                    <div class="site-analytics-summary__empty-state">
                                        <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ config.emptyStateLabelKey | transloco }}</span>
                                    </div>
                                }

                                <div class="site-analytics-summary__list-wrapper">
                                    @for (item of card.items; track item.label) {
                                        <div class="site-analytics-summary__list-item">
                                            <span class="tw-truncate">{{ item.label }}</span>
                                            <span class="tw-text-sm tw-text-color-secondary">{{ formatPercentage(item.percentage) }}</span>
                                        </div>
                                    }
                                </div>

                                <ng-template #footer>
                                    <div class="site-analytics-summary__footer-link">
                                        <a [uiSref]="card.overviewState" class="gel-link">
                                            {{ card.overviewLinkLabelKey | transloco }}
                                        </a>
                                    </div>
                                </ng-template>
                            </p-card>
                        }
                    </div>
                }

                @if (config.mode === 'sources') {
                    <div class="site-analytics-summary__metric-selector" data-test="site-analytics-summary-sources-metrics">
                        <p class="tw-mb-2 tw-font-medium">{{ (config.sourcesMetricsLabelKey || '') | transloco }}</p>
                        <div class="site-analytics-summary__metric-buttons">
                            @for (option of sourceMetricOptions; track option.value) {
                                <button
                                    type="button"
                                    class="site-analytics-summary__metric-button"
                                    [class.site-analytics-summary__metric-button--active]="selectedSourceMetric === option.value"
                                    (click)="onSourceMetricChange(option.value)"
                                >
                                    <span class="site-analytics-summary__metric-label">{{ option.label }}</span>
                                    <span class="site-analytics-summary__metric-value">{{ getSourceMetricValue(option.value) }}</span>
                                </button>
                            }
                        </div>
                    </div>

                    <p-card styleClass="tw-mb-6" data-test="site-analytics-summary-sources-chart-card">
                        <ng-template #title>
                            <div class="tw-text-lg tw-font-medium">{{ (config.sourcesChartTitleKey || '') | transloco }}</div>
                        </ng-template>

                        <div class="site-analytics-summary__chart-container site-analytics-summary__chart-container--large">
                            <p-chart
                                [type]="sourceChartType"
                                [data]="sourceChartData"
                                [options]="sourceChartOptions"
                                height="100%"
                            />
                            @if (!hasSourceChartData) {
                                <div class="site-analytics-summary__chart-empty-overlay">
                                    <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ config.emptyStateLabelKey | transloco }}</span>
                                </div>
                            }
                        </div>
                    </p-card>

                    <div class="tw-text-xl tw-font-medium tw-mb-4">
                        {{ (config.sourcesReferringSectionTitleKey || '') | transloco }}
                    </div>

                    <div class="site-analytics-summary__sources-grid" data-test="site-analytics-summary-sources-grid">
                        @for (card of sourceReferrerCards; track card.id) {
                            <p-card styleClass="tw-h-full">
                                <ng-template #title>
                                    <div class="tw-text-lg tw-font-medium">{{ card.titleKey | transloco }}</div>
                                </ng-template>

                                @if (card.items.length > 0) {
                                    <div class="site-analytics-summary__list-wrapper">
                                        @for (item of card.items; track item.label) {
                                            <div class="site-analytics-summary__sources-list-item">
                                                <div class="tw-flex tw-justify-between tw-gap-2">
                                                    <span class="tw-truncate">{{ item.label }}</span>
                                                    <span>{{ formatNumber(item.visits) }}</span>
                                                </div>
                                                <p-progressbar [value]="item.percentage" [showValue]="false" />
                                            </div>
                                        }
                                    </div>
                                } @else {
                                    <div class="site-analytics-summary__empty-state">
                                        <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ config.emptyStateLabelKey | transloco }}</span>
                                    </div>
                                }

                                <ng-template #footer>
                                    <div class="site-analytics-summary__footer-link">
                                        <a [uiSref]="card.overviewState" class="gel-link">
                                            {{ card.overviewLinkLabelKey | transloco }}
                                        </a>
                                    </div>
                                </ng-template>
                            </p-card>
                        }

                        <p-card styleClass="tw-h-full">
                            <ng-template #title>
                                <div class="tw-text-lg tw-font-medium">{{ (config.sourceTopKeywordsTitleKey || '') | transloco }}</div>
                            </ng-template>

                            @if (sourceKeywords.length > 0) {
                                <p-table [value]="sourceKeywords" [styleClass]="'op-card-table'">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th scope="col">{{ 'siteAnalytics.summary.sources.topKeywords.columns.keyword' | transloco }}</th>
                                            <th class="tw-text-right" scope="col">{{ 'siteAnalytics.summary.sources.topKeywords.columns.visits' | transloco }}</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-keyword>
                                        <tr>
                                            <td>{{ keyword.word }}</td>
                                            <td class="tw-text-right">{{ formatNumber(keyword.visits) }}</td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            } @else {
                                <div class="site-analytics-summary__empty-state">
                                    <span class="tw-text-gray-500 tw-font-[300] tw-text-xl">{{ config.emptyStateLabelKey | transloco }}</span>
                                </div>
                            }
                        </p-card>
                    </div>
                }
            }
        </mon-page-layout>
    </div>
</div>
